import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { AuditEntryDto } from '@markant-platform/shared/audit/data-access';
import { AuditChangeItem, ChangeSetTransformer } from './model/audit.config';
import { UserChangeSetFields, UserTransformerOptions } from './user-changeset.options';
import { Router } from '@angular/router';
import { MarkantSelectOption } from '@markant-platform/shared/util-helpers';
import { Filter } from '@markant-platform/shared/ui-filter';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { take, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserChangeSetTransformer implements ChangeSetTransformer {
  private static ORGANIZATION_ID_PARAM = 'organizationId';
  private organizationId: string | undefined;
  private fields: Record<UserChangeSetFields, string> = UserTransformerOptions.displayFields;

  private authenticatedUserService: AuthenticatedUserService = inject(AuthenticatedUserService);
  private translocoService: TranslocoService = inject(TranslocoService);
  private router: Router = inject(Router);

  public transform = (items: AuditEntryDto[]): AuditChangeItem[] => {
    this.getOrganizationId();
    this.updateFieldsWithOrgId();
    return items.flatMap(({ changeSet, modifiedBy, modifiedAt, reason }) => {
      if (!changeSet) {
        return [];
      }

      return Object.keys(changeSet)
        .filter(this.shouldDisplayField)
        .filter(this.filterForUserOrganization)
        .map(field => {
          const { previousValue, currentValue } = changeSet[field];

          return {
            key: field,
            label: this.translateField(field),
            previousValue: this.translate(field, previousValue),
            currentValue: this.translate(field, currentValue),
            modifiedBy,
            modifiedAt,
            reason,
          };
        });
    });
  };

  public readonly provideFilters = (): Filter<MarkantSelectOption> => {
    this.getOrganizationId();
    const filterOptions = Object.entries(UserTransformerOptions.displayFields).map(
      ([key, value]) =>
        ({
          value: key.replace(UserChangeSetTransformer.ORGANIZATION_ID_PARAM, this.organizationId as string),
          representation: this.translocoService.translate(value),
        }) as MarkantSelectOption
    );
    return {
      field: {
        groupName: 'field',
        translationKey: 'audit.table.filter.attribute',
        filterOptions,
      },
    };
  };

  private shouldDisplayField = (field: string): boolean => {
    const key = field.replace(
      this.organizationId as string,
      UserChangeSetTransformer.ORGANIZATION_ID_PARAM
    ) as UserChangeSetFields;
    return !!this.fields[key];
  };

  /**
   * Filters the changeSet entries to only show organization related data for the current organization.
   * @param field display attribute name
   */
  private filterForUserOrganization = (field: string): boolean => {
    const key = field.replace(
      this.organizationId as string,
      UserChangeSetTransformer.ORGANIZATION_ID_PARAM
    ) as UserChangeSetFields;
    return (
      !key.includes(UserChangeSetTransformer.ORGANIZATION_ID_PARAM) || field.includes(this.organizationId as string)
    );
  };

  private translateField = (field: string): string => {
    const key = field.replace(
      this.organizationId as string,
      UserChangeSetTransformer.ORGANIZATION_ID_PARAM
    ) as UserChangeSetFields;
    return this.translocoService.translate(this.fields[key]);
  };

  private translate = (field: string, value?: string): string | undefined => {
    if (!value) {
      return;
    }

    const key = field.replace(
      this.organizationId as string,
      UserChangeSetTransformer.ORGANIZATION_ID_PARAM
    ) as UserChangeSetFields;

    const translation = UserTransformerOptions.translatableValues[key];
    const isoTranslation = UserTransformerOptions.isoValues[key] as Intl.DisplayNamesType;

    if (translation) {
      return this.translocoService.translate(`${translation}.${value}`);
    }

    if (isoTranslation) {
      const lang = this.translocoService.getActiveLang();
      const intl = new Intl.DisplayNames(lang, { type: isoTranslation });
      return intl.of(value);
    }

    return value;
  };

  private getOrganizationId(): void {
    let route = this.router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }

    const orgId = route.snapshot.paramMap.get(UserChangeSetTransformer.ORGANIZATION_ID_PARAM);

    if (orgId) {
      this.organizationId = orgId;
    } else {
      this.authenticatedUserService
        .getOrganizationId()
        .pipe(
          tap(orgId => (this.organizationId = orgId)),
          take(1)
        )
        .subscribe();
    }
  }

  private updateFieldsWithOrgId(): void {
    for (const [key, value] of Object.entries(this.fields)) {
      const keyChanged = key.replace(
        this.organizationId as string,
        UserChangeSetTransformer.ORGANIZATION_ID_PARAM
      ) as UserChangeSetFields;
      this.fields[keyChanged] = value;
    }
  }
}
